import { EMPTY_GERBER_FILE_MAPPING, ExtrepEntry } from "../../index";
import _ from "lodash";
import { ZippedFiles } from "../readGerberZip";
import {
  EXCEL_EXTENSION,
  getFileColumns,
} from "../../../../../utils/parseFile";
import {
  BOMFileInfo,
  PNPFileInfo,
  UploadConfig,
} from "../../../../../reducers/uploadedBoard";

export const PROFILE = "profile";
export const COPPER_TOP = "copper_top";
export const COPPER_INNER_LAYERS = "copper_inner_layers";
export const COPPER_BOTTOM = "copper_bottom";
export const SOLDERMASK_TOP = "soldermask_top";
export const SOLDERMASK_BOTTOM = "soldermask_bottom";
export const SOLDERPASTE_TOP = "solderpaste_top";
export const SOLDERPASTE_BOTTOM = "solderpaste_bottom";
export const SILKSCREEN_TOP = "silkscreen_top";
export const SILKSCREEN_BOTTOM = "silkscreen_bottom";

const KICAD_MAPPING: { [identifier: string]: string } = {
  "Edge.Cuts": PROFILE,
  "F.Cu": COPPER_TOP,
  "In[0-9]+.Cu": COPPER_INNER_LAYERS,
  "B.Cu": COPPER_BOTTOM,
  "F.Mask": SOLDERMASK_TOP,
  "B.Mask": SOLDERMASK_BOTTOM,
  "F.Paste": SOLDERPASTE_TOP,
  "B.Paste": SOLDERPASTE_BOTTOM,
  "F.Silk": SILKSCREEN_TOP,
  "B.Silk": SILKSCREEN_BOTTOM,
};

interface PnPColumns {
  designator_column: string | undefined;
  x_column: string | undefined;
  y_column: string | undefined;
  unit: string | undefined;
  rotation_column: string | undefined;
  mount_type_column: string | undefined;
}

interface BOMColumns {
  designator_column: string | undefined;
  mpn_column: string | undefined;
  manufacturer_column: string | undefined;
  footprint_column: string | undefined;
  value_text_column: string | undefined;
}

export const getPnPColumnSuggestions = (pnpColumns: string[]): PnPColumns => {
  let detectedUnit = undefined;

  pnpColumns.forEach((column) => {
    if (column.toLowerCase().includes("mm")) {
      detectedUnit = "mm";
    } else if (column.toLowerCase().includes("mil")) {
      detectedUnit = "milinch";
    } else if (column.toLowerCase().includes("inch")) {
      detectedUnit = "inch";
    }
  });

  return {
    designator_column: pnpColumns.find(
      (column) =>
        column.toLowerCase().includes("ref") ||
        column.toLowerCase().includes("designator"),
    ),
    x_column: pnpColumns.find((column) => column.toLowerCase().includes("x")),
    y_column: pnpColumns.find((column) => {
      return (
        !column.toLowerCase().includes("layer") &&
        column.toLowerCase().includes("y")
      );
    }),
    unit: detectedUnit,
    rotation_column: pnpColumns.find((column) =>
      column.toLowerCase().includes("rot"),
    ),
    mount_type_column: pnpColumns.find(
      (column) =>
        column.toLowerCase().includes("layer") ||
        column.toLowerCase().includes("side"),
    ),
  };
};

export const getBOMColumnSuggestions = (bomColumns: string[]): BOMColumns => {
  return {
    designator_column: bomColumns.find(
      (column) =>
        column.toLowerCase().includes("ref") ||
        column.toLowerCase().includes("designator"),
    ),
    mpn_column: bomColumns.find(
      (column) =>
        column.toLowerCase().includes("part") ||
        column.toLowerCase().includes("mpn"),
    ),
    manufacturer_column: bomColumns.find(
      (column) =>
        column.toLowerCase().includes("manufacturer") &&
        !column.toLowerCase().includes("part"),
    ),
    footprint_column: bomColumns.find(
      (column) =>
        column.toLowerCase().includes("footprint") ||
        column.toLowerCase().includes("package"),
    ),
    value_text_column: bomColumns.find(
      (column) =>
        column.toLowerCase().includes("value") ||
        column.toLowerCase().includes("description"),
    ),
  };
};

const containsSide = (filename: string, side: "top" | "bottom") => {
  return (
    filename.toLowerCase().includes(side) ||
    filename.toLowerCase().includes(side.slice(0, 3))
  );
};

const contains = (
  filename: string,
  oneOf: string[],
  side?: "top" | "bottom",
) => {
  const filenameWithoutDirs = filename.split("/").at(-1);
  const matchesOneOf =
    filenameWithoutDirs !== undefined &&
    oneOf.some((one) =>
      filenameWithoutDirs.toLowerCase().includes(one.toLowerCase()),
    );
  if (side === undefined) {
    return (
      !containsSide(filename, "top") &&
      !containsSide(filename, "bottom") &&
      matchesOneOf
    );
  } else {
    return containsSide(filename, side) && matchesOneOf;
  }
};

const SILKSCREEN_WORDS = ["silk", "legend"];

function gerberFileLayerBestMatch(
  filename: string,
  extrepInfo?: ExtrepEntry[],
): string | undefined {
  if (extrepInfo) {
    const description = extrepInfo.find((entry) =>
      filename.endsWith(entry.extension),
    )?.description;
    if (description) {
      const bestMatch = gerberFileLayerBestMatch(description);
      if (bestMatch) {
        return bestMatch;
      }
    }
  }
  const maybeKicadFileIdentifier = Object.keys(KICAD_MAPPING).find(
    (kicadFileIdentifier) => {
      return filename.match(new RegExp(kicadFileIdentifier, "i"));
    },
  );
  if (maybeKicadFileIdentifier !== undefined) {
    return KICAD_MAPPING[maybeKicadFileIdentifier];
  }
  if (
    filename.endsWith("BoardEdgeRout.TXT") ||
    filename.endsWith(".GKO") ||
    contains(filename, ["profile", "outline"])
  ) {
    return PROFILE;
  } else if (
    filename.endsWith(".GTL") ||
    contains(filename, ["copper"], "top")
  ) {
    return COPPER_TOP;
  } else if (
    /copper_inner_l\d+.gbr$/.test(filename) ||
    /\.G\d+$/.test(filename) ||
    /\.GP\d+$/.test(filename)
  ) {
    return COPPER_INNER_LAYERS;
  } else if (
    filename.endsWith(".GBL") ||
    contains(filename, ["copper"], "bottom")
  ) {
    return COPPER_BOTTOM;
  } else if (
    filename.endsWith(".GTO") ||
    contains(filename, SILKSCREEN_WORDS, "top")
  ) {
    return SILKSCREEN_TOP;
  } else if (
    filename.endsWith(".GBO") ||
    contains(filename, SILKSCREEN_WORDS, "bottom")
  ) {
    return SILKSCREEN_BOTTOM;
  } else if (
    filename.endsWith(".GTP") ||
    contains(filename, ["paste"], "top")
  ) {
    return SOLDERPASTE_TOP;
  } else if (
    filename.endsWith(".GBP") ||
    contains(filename, ["paste"], "bottom")
  ) {
    return SOLDERPASTE_BOTTOM;
  } else if (filename.endsWith(".GTS") || contains(filename, ["mask"], "top")) {
    return SOLDERMASK_TOP;
  } else if (
    filename.endsWith(".GBS") ||
    contains(filename, ["mask"], "bottom")
  ) {
    return SOLDERMASK_BOTTOM;
  }
}

function matchesDrillLayer(filename: string) {
  if (contains(filename, ["map"])) {
    return false;
  }
  if (filename.endsWith("BoardEdgeRout.TXT")) {
    return false;
  }
  return (
    filename.endsWith("RoundHoles.TXT") ||
    filename.endsWith("SlotHoles.TXT") ||
    contains(filename, ["drill", "plated"]) ||
    filename.endsWith(".drl") ||
    filename.includes("PTH") ||
    filename.includes("NPTH")
  );
}

function parsesExtrepFile(
  extrepContent?: Blob | string,
): ExtrepEntry[] | undefined {
  if (extrepContent === undefined) {
    return;
  }
  return (extrepContent as string)
    .split("\n")
    .map((line) => line.split("  ").filter((part) => /\S/.test(part)))
    .filter((line) => line.length == 2 && line[0]!.startsWith("."))
    .map((line) => ({
      extension: line[0]!.trim(),
      description: line[1]!.trim(),
    }));
}

const mightBeBOMFile = (lowerCasePath: string) => {
  return lowerCasePath.includes("bom") || lowerCasePath.includes("bill");
};

const mightBePNPFile = (lowerCasePath: string) => {
  return lowerCasePath.includes("pnp") || lowerCasePath.includes("pick");
};
async function getAdditionalCustomerFileInfo(
  zippedFiles: ZippedFiles,
): Promise<{
  assemblyDrawing: string[];
  fabDrawing: string[];
  referenceFile: string[];
}> {
  const maybeAssemblyDrawings: string[] = [];
  const maybeFabDrawings: string[] = [];
  const additionalFiles: string[] = [];

  zippedFiles.additionalFiles.forEach(({ path }) => {
    const lowerCasePath = path.toLowerCase();
    if (lowerCasePath.includes("assembly")) {
      maybeAssemblyDrawings.push(path);
    } else if (lowerCasePath.includes("fab")) {
      maybeFabDrawings.push(path);
    } else if (
      !mightBeBOMFile(lowerCasePath) &&
      !mightBePNPFile(lowerCasePath)
    ) {
      additionalFiles.push(path);
    }
  });

  return {
    assemblyDrawing: maybeAssemblyDrawings,
    fabDrawing: maybeFabDrawings,
    referenceFile: additionalFiles,
  };
}
async function getBOMAndPNPFileInfo(zippedFiles: ZippedFiles): Promise<{
  bomFileInfo: BOMFileInfo | null;
  pnpFileInfo: PNPFileInfo | null;
}> {
  // Sort to make sure excel files are recommended before csv files
  const maybeBOMFile = zippedFiles.bomOrPNPFiles
    .sort((a, b) => {
      if (a.path.includes(EXCEL_EXTENSION) && !b.path.includes(EXCEL_EXTENSION))
        return -1;
      if (!a.path.includes(EXCEL_EXTENSION) && b.path.includes(EXCEL_EXTENSION))
        return 1;
      return 0;
    })
    .find(({ path }) => {
      const lowerCasePath = path.toLowerCase();
      return mightBeBOMFile(lowerCasePath);
    });
  if (maybeBOMFile !== undefined) {
    const bomColumnInfo = await getFileColumns(
      maybeBOMFile.path,
      maybeBOMFile.content as Blob,
    );
    const bomColumns = bomColumnInfo?.headers || [];
    const bomColumnSuggestions = getBOMColumnSuggestions(bomColumns);
    const bomFileInfo = {
      file_name: maybeBOMFile.path,
      header_row_index: bomColumnInfo?.headerRowIndex,
      ...bomColumnSuggestions,
    };
    const maybePNPFile = zippedFiles.bomOrPNPFiles.find(({ path }) => {
      const lowerCasePath = path.toLowerCase();
      return mightBePNPFile(lowerCasePath);
    });
    if (maybePNPFile !== undefined) {
      const pnpColumnInfo = await getFileColumns(
        maybePNPFile.path,
        maybePNPFile.content as Blob,
      );
      const pnpColumns = pnpColumnInfo?.headers || [];
      const pnpColumnSuggestions = getPnPColumnSuggestions(pnpColumns);
      const pnpFileInfo = {
        file_name: maybePNPFile.path,
        header_row_index: pnpColumnInfo?.headerRowIndex,
        ...pnpColumnSuggestions,
      };
      return {
        bomFileInfo,
        pnpFileInfo,
      };
    }
    return {
      bomFileInfo,
      pnpFileInfo: null,
    };
  }
  return {
    bomFileInfo: null,
    pnpFileInfo: null,
  };
}

export async function getBestMatchSuggestions(
  zippedFiles: ZippedFiles,
): Promise<UploadConfig> {
  const gerberFileMapping = _.cloneDeep(EMPTY_GERBER_FILE_MAPPING);
  const parsedExtrep = parsesExtrepFile(zippedFiles.extrepFile?.content);
  zippedFiles.gerberFiles.forEach(({ path }) => {
    const layerMatch = gerberFileLayerBestMatch(path, parsedExtrep);
    if (layerMatch !== undefined) {
      if (Array.isArray(gerberFileMapping[layerMatch])) {
        gerberFileMapping[layerMatch].push(path);
      } else {
        gerberFileMapping[layerMatch] = path;
      }
    }
  });
  zippedFiles.drillFiles.forEach(({ path }) => {
    if (matchesDrillLayer(path)) {
      (gerberFileMapping.drills as string[]).push(path);
    }
  });
  const { bomFileInfo, pnpFileInfo } = await getBOMAndPNPFileInfo(zippedFiles);
  const { assemblyDrawing, fabDrawing, referenceFile } =
    await getAdditionalCustomerFileInfo(zippedFiles);
  return {
    gerber_file_layer_mapping: gerberFileMapping,
    bom_file_info: bomFileInfo,
    pnp_file_info: pnpFileInfo,
    assembly_drawing: assemblyDrawing,
    fab_drawing: fabDrawing,
    reference_file: referenceFile,
  };
}
